import React, { useState, useEffect, useCallback } from "react";
import { Wrapper } from "./style";
import { MakeApiCall } from "../../../apis";
import { message, Table, Button, Dropdown, Menu, Upload, Pagination, ConfigProvider, Spin, Alert } from "antd";
import { DownloadOutlined, UploadOutlined, FileExcelOutlined, CloudUploadOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from 'dayjs';
import { Helmet } from "react-helmet";
import AddExpense from "./lib/AddExpense";
import { exportToExcel } from "../../../utils/excelExport";
import { Link } from "react-router-dom";
import { useDispatch } from 'react-redux';
import { setData as setDataAction } from "../../../redux/actions";
import SyncToast from '../../../components/toast/SyncToast';
import SetupStepsBanner from "../../../components/onboarding/Setupstepsbanner";
import FeatureLockedOverlay from "../../../components/modal/Featurelockedoverlay";

const Expenses = (props) => {
  const { userData, setData } = props;
  const dispatch = useDispatch();

  const isSpApiConnected = userData?.user?.user_data?.is_amz_sp_api_connected === 1;
  const isDataSyncing = isSpApiConnected && userData?.user?.user_data?.is_data_sync_email_sent === null;

  const [totalPage, setTotalPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);

  const [loading, setLoading] = useState(true);
  const [list, setList] = useState([]);
  const [marketplaceList, setMarketplaceList] = useState([]);
  const [searchText, setSearchText] = useState("");

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);

  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [syncToastDismissed, setSyncToastDismissed] = useState(false);

  useEffect(() => {
    if (isDataSyncing) setSyncToastDismissed(false);
  }, [isDataSyncing]);

  const getExpensesList = useCallback(async (data) => {
    if (!isSpApiConnected) { setLoading(false); return; }
    setLoading(true);
    const response = await MakeApiCall(
        `get-expenses?search_text=${data?.searchText || searchText}&page=${data?.page || page || 1}&per-page=${data?.pageSize || pageSize}`,
        "get", null, true, {}, userData?.token
    );

    if (response?.status) {
      const records = response?.data?.records || [];
      setTotalPage(response?.data?.pagination?.totalCount || 0);
      setPage(response?.data?.pagination?.page || 1);
      setList(records);
      setLoading(false);

      if (userData) {
        try {
          const hasExpenses = Array.isArray(records) && records.length > 0;
          if (userData?.user?.user_data?.is_expense_data_added !== hasExpenses) {
            const newUserData = {
              ...userData,
              user: { ...userData.user, user_data: { ...userData.user.user_data, is_expense_data_added: hasExpenses } },
            };
            if (typeof setData === 'function') setData(newUserData);
            if (typeof dispatch === 'function') dispatch(setDataAction(newUserData));
          }
        } catch (e) { console.error('Error updating userData:', e); }
      }
    } else {
      message.warning(response?.message);
      setLoading(false);
      setList([]);
    }
  }, [isSpApiConnected, searchText, page, pageSize, userData, dispatch, setData]);

  const handleExport = async () => {
    if (!isSpApiConnected) return;
    setExporting(true);
    try {
      const response = await MakeApiCall(`get-expenses?search_text=${encodeURIComponent(searchText || "")}&no_pagination=true`, "get", null, true, {}, userData?.token);
      if (response?.status) {
        const records = response?.data?.records || [];
        if (!records || records.length === 0) {
          message.warning("No records found to export");
        } else {
          const formatted = records.map((r) => {
            let sku = "";
            if (r?.products) {
              if (Array.isArray(r.products)) sku = r.products.map((p) => (p?.sku || p?.asin || p || "")).join(", ");
              else if (typeof r.products === 'object') sku = r.products.sku || r.products.asin || r.products.name || '';
              else sku = String(r.products);
            }
            let duration = "";
            if (r?.end_date) {
              try { duration = `${dayjs(r.end_date).diff(dayjs(r.start_date), 'day')} days`; } catch (e) { duration = ""; }
            }
            return {
              'Start Date': r?.start_date || "",
              'Frequency': r?.type || "",
              'Expense Title': r?.name || "",
              'Category': r?.category || "",
              'SKU': sku,
              'Marketplace': r?.marketplace_ids || "",
              'Amount': r?.amount || "",
              'Currency': r?.currency || "",
              'Duration': duration,
              '# of occurrences (optional)': r?.no_of_occurrences || "",
              'Amortize expense daily': r?.amortize_expense_daily ? 'Yes' : 'No',
              'Is Ad Cost?': r?.show_under_advertising_cost ? 'Yes' : 'No',
            };
          });
          exportToExcel(formatted, {}, "expenses.xlsx");
          message.success("Export started");
        }
      }
    } catch (err) { message.error("Export error occurred."); } finally { setExporting(false); }
  };

  const MarketplaceListAction = useCallback(async () => {
    if (!isSpApiConnected) return;
    setLoading(true);
    const response = await MakeApiCall(`get-marketplaces`, `get`, null, true, null, userData?.token);
    if (response?.status) {
      setMarketplaceList(response.data.all_marketplaces?.map((d) => ({ label: d?.sales_channel, value: d?.id })) || []);
    }
    setLoading(false);
  }, [isSpApiConnected, userData]);

  useEffect(() => { if (isSpApiConnected) getExpensesList({ page, pageSize }); }, [getExpensesList, isSpApiConnected, page, pageSize]);
  useEffect(() => { if (isSpApiConnected) MarketplaceListAction(); }, [MarketplaceListAction, isSpApiConnected]);

  const columns = [
    { title: "#", key: "index", render: (text, record, index) => (page - 1) * pageSize + index + 1 },
    { title: "Date", dataIndex: "start_date", key: "start_date", sorter: (a, b) => (a.start_date || "").localeCompare(b.start_date || "") },
    { title: "Type", dataIndex: "type", key: "type", render: (type) => (type !== null ? type : "N/A"), sorter: (a, b) => (a.type || "").localeCompare(b.type || "") },
    { title: "Name", dataIndex: "name", key: "name", render: (name) => (name !== null ? name : "N/A"), sorter: (a, b) => (a.name || "").localeCompare(b.name || "") },
    { title: "Category", dataIndex: "category", key: "category", render: (category) => (category !== null ? category : "N/A"), sorter: (a, b) => (a.category || "").localeCompare(b.category || "") },
    { title: "Advertising cost", dataIndex: "show_under_advertising_cost", key: "show_under_advertising_cost", render: (val) => val ? "Yes" : "No", sorter: (a, b) => String(a.show_under_advertising_cost).localeCompare(String(b.show_under_advertising_cost)) },
    { title: "Product", dataIndex: "products", key: "products", width: 250, render: (products) => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word', minWidth: '200px', lineHeight: '1.5' }}>{products || "N/A"}</div> },
    { title: "Marketplace", dataIndex: "marketplace_ids", key: "marketplace_ids", render: (m) => m || "N/A" },
    { title: "Amount", dataIndex: "amount", key: "amount", render: (_, r) => `${r?.currency} ${r?.amount ?? "N/A"}`, sorter: (a, b) => a.amount - b.amount },
    { title: "Action", key: "action", render: (_, row) => (
          <Dropdown overlay={menu1(row)} disabled={!isSpApiConnected}>
            <Button type="link" disabled={!isSpApiConnected}><i className="ki-solid ki-dots-vertical fs-1 text-gray-800 "></i></Button>
          </Dropdown>
      )},
  ];

  const menu1 = (row) => (
      <Menu className="min-w-200px p-3">
        <Menu.Item key="1" className="d-flex align-items-center mb-2" onClick={() => handleEdit(row)}><i className="ki-outline ki-pencil fs-3 me-2"></i>Edit Expenses</Menu.Item>
        <Menu.Item key="2" className="d-flex align-items-center" onClick={() => handleDelete(row?.id)}><i className="ki-outline ki-trash fs-3 me-2"></i>Delete Expenses</Menu.Item>
      </Menu>
  );

  const handleUpload = (file) => {
    if (!isSpApiConnected) return false;
    const rawFile = file.originFileObj || file;
    setUploadedFile(rawFile);
    message.success(`${rawFile.name} ready.`);
    return false;
  };

  const handleApplyClick = async () => {
    if (!isSpApiConnected || !uploadedFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadedFile);
      const response = await MakeApiCall("import-expenses", "post", formData, true, {}, userData?.token);
      if (response?.status) {
        message.success("File imported!");
        setUploadedFile(null);
        getExpensesList();
      }
    } catch (error) { message.error("Upload failed."); } finally { setUploading(false); }
  };

  const importMenu = (
      <Menu className="p-2 shadow-sm" style={{ borderRadius: '8px', minWidth: '250px' }}>
        <div className="px-3 py-2 border-bottom"><span className="fw-bold text-gray-800 fs-7">Import Expense Data</span></div>
        <div className="px-3 py-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <span className="fs-7">New to this?</span>
            <a href="/assets/media/templates/Expense_Import_Template.xlsx" download="expenses_template.xlsx" className="badge badge-light-danger text-danger fw-bold py-2 border-0">
              <DownloadOutlined className="me-1" /> Template
            </a>
          </div>
          <Upload beforeUpload={handleUpload} fileList={uploadedFile ? [uploadedFile] : []} onRemove={() => setUploadedFile(null)} maxCount={1} disabled={!isSpApiConnected}>
            <Button className="w-100 fs-7 h-45px border-dashed border-danger bg-light-danger bg-opacity-10 text-danger" disabled={!isSpApiConnected}>
              <CloudUploadOutlined /> {uploadedFile ? 'Change File' : 'Select CSV/Excel'}
            </Button>
          </Upload>
        </div>
        <Menu.Divider />
        <div className="px-3 py-2 d-flex gap-2">
          <Button size="small" className="flex-grow-1" onClick={() => setUploadedFile(null)}>Reset</Button>
          <Button size="small" type="primary" danger className="flex-grow-1" onClick={handleApplyClick} disabled={uploading || !uploadedFile || !isSpApiConnected}>{uploading ? 'Processing...' : 'Apply Import'}</Button>
        </div>
      </Menu>
  );

  const handleSearch = (e) => { setSearchText(e.target.value); setPage(1); };
  const handleCancel = () => setIsModalVisible(false);

  const handleSubmit = async (values) => {
    if (!isSpApiConnected) return;
    setLoading(true);
    const response = await MakeApiCall(editingExpense ? `update-expense/${editingExpense.id}` : "add-expense", "post", values, true, {}, userData?.token);
    if (response?.status) {
      message.success("Saved!");
      setIsModalVisible(false);
      getExpensesList();
    }
    setLoading(false);
  };

  const handleEdit = (expense) => { setEditingExpense(expense); setIsModalVisible(true); };
  const handleDelete = async (id) => {
    if (!isSpApiConnected) return;
    setLoading(true);
    const response = await MakeApiCall(`delete-expense/${id}`, "delete", null, true, {}, userData?.token);
    if (response?.status) { message.success("Deleted!"); getExpensesList(); }
    setLoading(false);
  };

  return (
      <Wrapper>
        <Helmet><title>Expenses</title></Helmet>
        <SetupStepsBanner userData={userData} />

        <SyncToast visible={isDataSyncing && !syncToastDismissed} onDismiss={() => setSyncToastDismissed(true)} />

        <div style={{ position: 'relative' }}>
          <FeatureLockedOverlay locked={!isSpApiConnected} title="Features Locked" message="Connect your Amazon account to unlock analytics."/>

          <div className="mb-10 mx-n9 px-10 py-5 bg-white filter">
            <div className="position-relative">
              <i className="ki-outline ki-magnifier text-gray-800 fs-2 position-absolute top-50 translate-middle-y ms-4" />
              <input type="text" value={searchText} onChange={handleSearch} disabled={!isSpApiConnected} className="form-control form-control-solid text-dark w-300px border border-gray-300 fs-7 ps-12 all-search" placeholder="Search by sku, title or asin..." />
            </div>
          </div>

          <div className="row gy-5 g-xl-5">
            <div className="col-xxl-12">
              <div className="card card-xxl-stretch mb-5 mb-xl-8">
                <div className="card-header border-bottom-dashed">
                  <h3 className="card-title fw-bolder">Expenses List</h3>
                  <div className="card-toolbar gap-2">
                    <button className="btn btn-sm btn-danger fs-7 fw-bold" disabled={!isSpApiConnected} onClick={() => { setEditingExpense(null); setIsModalVisible(true); }}>
                      <PlusOutlined className="me-1" /> Add Expense
                    </button>
                    <div className="bullet bullet-vertical h-25px bg-gray-300 mx-1"></div>
                    <Dropdown overlay={importMenu} placement="bottomRight" disabled={!isSpApiConnected}>
                      <button className="btn btn-sm btn-light-danger fs-7 fw-bold" disabled={!isSpApiConnected}>
                        Import <i className="ms-1 bi bi-download fs-6" />
                      </button>
                    </Dropdown>
                    <button className="btn btn-sm btn-secondary fs-7 fw-bold border text-gray-700" onClick={handleExport} disabled={exporting || !isSpApiConnected}>
                      {exporting ? <Spin size="small" /> : <><i className="bi bi-upload fs-6 me-1" /> Export</>}
                    </button>
                  </div>
                </div>
                <div className="card-body py-2">
                  <div className="table-responsive">
                    <Table scroll={{ x: "max-content" }} columns={columns} loading={loading} dataSource={list} rowKey="id" pagination={false}
                           components={{
                             table: (p) => <table className="table table-row-dashed table-row-gray-300 align-middle gs-4 gy-4 fs-7" {...p} />,
                             header: { row: (p) => <tr {...p} className="fw-bolder text-dark bg-cus" /> }
                           }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row gy-5 g-xl-5">
            <div className="col-xxl-12">
              <div className="card card-xxl-stretch mb-5 mb-xl-8">
                <div className="card-body py-2">
                  <ConfigProvider theme={{ token: { colorPrimary: '#f45b68' } }}>
                    <Pagination className="mt-3" current={page} pageSize={pageSize} total={totalPage} disabled={!isSpApiConnected} showSizeChanger
                                onChange={(p, s) => { setPage(p); setPageSize(s); }}
                                showTotal={(t, r) => `${r[0]}-${r[1]} of ${t} items`}
                    />
                  </ConfigProvider>
                </div>
              </div>
            </div>
          </div>
        </div>

        <AddExpense isModalVisible={isModalVisible} handleCancel={handleCancel} handleSubmit={handleSubmit} marketplaceList={marketplaceList} userData={userData} refreshExpenses={getExpensesList} editingExpense={editingExpense} />
      </Wrapper>
  );
};

export default Expenses;